import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Key as KeyIcon } from '@mui/icons-material';
import { passkeyService } from '../../services/passkey';
import { deviceInfoService } from '../../services/deviceInfo';
import { auth } from '../../config/firebase';
import { format } from 'date-fns';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { AuthenticatorMetadata } from '../../types/auth';

const neonGreen = '#39FF14';
const darkBg = '#0a0a0a';

export const PasskeyManager: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authenticators, setAuthenticators] = useState<AuthenticatorMetadata[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  useEffect(() => {
    const loadPasskeys = async () => {
      if (!auth.currentUser?.email) return;

      try {
        const passkeys = await passkeyService.listPasskeys(auth.currentUser.email);
        const devices = auth.currentUser.email ? 
          await deviceInfoService.getUserDevices(auth.currentUser.email) : [];

        setAuthenticators(passkeys.map(pk => {
          const device = devices.find(d => d.authMethods.includes('passkey'));
          return {
            id: pk.credentialId,
            name: device?.deviceName || 'Unknown Device',
            dateRegistered: pk.createdAt,
            lastUsedAt: pk.lastUsedAt,
            type: 'public-key'
          };
        }));
      } catch (error) {
        console.error('Error loading passkeys:', error);
        setError('Failed to load passkeys');
      }
    };

    loadPasskeys();
  }, [auth.currentUser?.email]);

  const handleRegisterPasskey = async () => {
    if (!auth.currentUser?.email) {
      setError('You must be logged in to register a passkey');
      return;
    }

    try {
      setIsRegistering(true);
      const email = auth.currentUser.email;
      
      const registeredPasskey = await passkeyService.register({
        email
      });

      // Update user's passkey credentials in Firestore
      const userRef = doc(db, 'users', email);
      await updateDoc(userRef, {
        'authMethods.passkey.credentials': arrayUnion(registeredPasskey)
      });

      // Register the device
      await deviceInfoService.registerDeviceForUser(email, 'passkey');

      // Reload passkeys to get updated list with device info
      const passkeys = await passkeyService.listPasskeys(email);
      const devices = await deviceInfoService.getUserDevices(email);

      setAuthenticators(passkeys.map(pk => {
        const device = devices.find(d => d.authMethods.includes('passkey'));
        return {
          id: pk.credentialId,
          name: device?.deviceName || 'New Device',
          dateRegistered: pk.createdAt,
          lastUsedAt: pk.lastUsedAt,
          type: 'public-key'
        };
      }));

      setOpenDialog(false);
    } catch (error) {
      console.error('Error registering passkey:', error);
      setError(error instanceof Error ? error.message : 'Failed to register passkey');
    } finally {
      setIsRegistering(false);
    }
  };

  const handleRemovePasskey = async (credentialId: string) => {
    if (!auth.currentUser?.uid) {
      setError('You must be logged in to remove a passkey');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await passkeyService.removePasskey(auth.currentUser.uid, credentialId);
      await loadAuthenticators();
    } catch (error) {
      console.error('Error removing passkey:', error);
      setError(error instanceof Error ? error.message : 'Failed to remove passkey');
    } finally {
      setLoading(false);
    }
  };

  const loadAuthenticators = async () => {
    if (!auth.currentUser?.email) return;
    
    try {
      const email = auth.currentUser.email;
      const passkeys = await passkeyService.listPasskeys(email);
      const devices = await deviceInfoService.getUserDevices(email);

      setAuthenticators(passkeys.map(pk => {
        const device = devices.find(d => d.authMethods.includes('passkey'));
        return {
          id: pk.credentialId,
          name: device?.deviceName || 'Unknown Device',
          dateRegistered: pk.createdAt,
          lastUsedAt: pk.lastUsedAt,
          type: 'public-key'
        };
      }));
    } catch (error) {
      console.error('Error loading authenticators:', error);
      setError('Failed to load authenticators');
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, backgroundColor: darkBg }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: neonGreen }}>
          Passkey
        </Typography>
        {authenticators.length === 0 && (
          <Tooltip title="Set up passkey for this device">
            <Button
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              variant="contained"
              sx={{ 
                backgroundColor: neonGreen,
                color: 'black',
                '&:hover': {
                  backgroundColor: '#2bff00'
                }
              }}
            >
              Setup Passkey
            </Button>
          </Tooltip>
        )}
      </Box>

      {authenticators.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
          No passkey configured for this device. Setting up a passkey enables secure, passwordless login.
        </Typography>
      ) : (
        <List>
          {authenticators.map((auth) => (
            <ListItem key={auth.id}>
              <ListItemText
                primary={auth.name}
                secondary={`Registered: ${format(new Date(auth.dateRegistered), 'PPp')}`}
                sx={{ color: 'white' }}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Remove passkey from this device">
                  <IconButton
                    edge="end"
                    onClick={() => handleRemovePasskey(auth.id)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        Passkeys are device-specific and provide the highest level of security. You'll need to set up alternative authentication methods for other devices.
      </Alert>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Register New Passkey</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Register a new passkey for this device. Your device's information will be automatically detected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleRegisterPasskey} disabled={isRegistering}>
            {isRegistering ? 'Registering...' : 'Register'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
